import { useQuery } from "@tanstack/react-query";
import { MeetingStatusEnum } from "enums/MeetingStatusEnum";
import { toServerDate } from "helpers";
import { createLogger } from "logging";
import { PatchMeetingModel } from "models/PatchMeetingModel";
import { useApi, useMeetings, usePermissions, useToasts, useTranslations } from "providers";
import { useMeeting } from "providers";
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { EditMeetingPanel } from "./components/EditMeetingPanel";
import { EditMeetingContext, getMeetingFieldValues, MeetingFieldValues } from "./EditMeetingContext";

const logger = createLogger("EditMeetingContext");

export const EditMeetingProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const { handleHttpError } = usePermissions();
  const { reloadMeeting, setMeetingUpdating } = useMeeting();
  const { reloadMeetings } = useMeetings();
  const { meetingApi } = useApi();
  const i18n = useTranslations();
  const toasts = useToasts();

  const [confirmMeetingUnsaved, setConfirmMeetingUnsaved] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [editMeetingId, setEditMeetingId] = useState<number>(-1);

  const form = useForm<MeetingFieldValues>({
    defaultValues: undefined,
  });
  const { formState, getValues, reset } = form;

  // Load meeting using React Query
  const queryResult = useQuery({
    queryKey: [`Meeting|${editMeetingId}|Edit`],
    queryFn: async () => {
      const data = await meetingApi.getItem(editMeetingId);
      const fieldValues = getMeetingFieldValues(data);
      reset(fieldValues);
      logger.trace("Loaded", data);
      return data;
    },
    enabled: editMeetingId > 0,
    refetchOnWindowFocus: false,
  });
  const { error } = queryResult;
  const isLoading = queryResult.isFetching;

  useEffect(() => {
    handleHttpError(error);
  }, [error, handleHttpError]);

  const watchErrors = JSON.stringify(formState.errors, null, 2);
  const { isDirty, isValid } = formState;
  const isSubmittable = useMemo(
    () => (!!editMeetingId && isValid) || watchErrors == "{}",
    [editMeetingId, isValid, watchErrors]
  );

  const handleClose = useCallback(
    (ignoreDirty?: boolean) => {
      const confirm = isDirty && !ignoreDirty;
      logger.debug(`handleClose(): isDirty:=${isDirty} (ignore:=${ignoreDirty}) -> ${confirm}`);
      if (confirm) {
        setConfirmMeetingUnsaved(true);
      } else {
        setIsSubmitting(false);
        setEditMeetingId(-1);
      }
    },
    [isDirty, setEditMeetingId]
  );

  const handleSuccess = useCallback(
    (title: string) => {
      toasts.updateSuccess(i18n.Meeting, title);
      setIsSubmitting(false);
      setEditMeetingId(-1);
      reloadMeeting();
      reloadMeetings();
    },
    [i18n, toasts, reloadMeeting, reloadMeetings]
  );

  const handleError = useCallback(
    (title: string, error: unknown) => {
      toasts.updateFailure(i18n.Meeting, title, error);
      throw error;
    },
    [i18n, toasts]
  );

  const getPatch = useCallback(
    (values: MeetingFieldValues) => {
      const { title, location, note, status, locked, isTemplate } = values;
      const eventDate = toServerDate(values.eventDate);
      const endDate = toServerDate(values.endDate);
      const submissionDate = toServerDate(values.submissionDate);

      const patch: PatchMeetingModel = {
        id: editMeetingId,
        eventDate,
        endDate,
        submissionDate,
        location,
        note,
        isTemplate,
        status: MeetingStatusEnum[status],
        locked,
        title,
      };
      return patch;
    },
    [editMeetingId]
  );

  const handleSave = useCallback(async () => {
    logger.debug(`handleSave(): `, editMeetingId, isSubmittable);
    if (!editMeetingId) return;
    if (!isSubmittable) return;

    try {
      setIsSubmitting(true);
      setMeetingUpdating(editMeetingId, true);
      const patch = getPatch(getValues());
      const { title = "" } = patch;

      try {
        await meetingApi.update(patch);
        handleSuccess(title);
      } catch (error) {
        handleError(title, error);
      }
    } catch (error) {
      logger.error("Error saving meeting:", error);
    } finally {
      setIsSubmitting(false);
      setMeetingUpdating(editMeetingId, false);
    }
  }, [isSubmittable, editMeetingId, setMeetingUpdating, meetingApi, getValues, getPatch, handleSuccess, handleError]);

  const contextValue = useMemo(
    () => ({
      confirmMeetingUnsaved,
      setConfirmMeetingUnsaved,
      editMeetingId,
      setEditMeetingId,
      isLoading,
      isDirty,
      isValid,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
    }),
    [
      confirmMeetingUnsaved,
      editMeetingId,
      isLoading,
      isDirty,
      isValid,
      isSubmittable,
      isSubmitting,
      form,
      getPatch,
      handleSave,
      handleClose,
    ]
  );

  return (
    <EditMeetingContext.Provider value={contextValue}>
      {children}
      <EditMeetingPanel />
    </EditMeetingContext.Provider>
  );
};
